import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section, Empty } from "@/components/common";
import { ChatBox } from "@/components/ChatBox";
import { QrScanner } from "@/components/QrScanner";

export default function Messages() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [code, setCode] = useState("");
  const [scan, setScan] = useState(false);

  const loadThreads = () => api.get("/dm/threads").then((r) => setThreads(r.data)).catch(() => {});
  const loadMsgs = () => { if (sel) api.get(`/dm/${sel.id}/messages`).then((r) => setMsgs(r.data)).catch(() => {}); };
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 8000); return () => clearInterval(t); }, []);
  useEffect(() => { loadMsgs(); const t = setInterval(loadMsgs, 5000); return () => clearInterval(t); }, [sel]); // eslint-disable-line
  useEffect(() => {
    const u = params.get("u");
    if (u && !sel) api.get(`/dm/${u}/messages`).then(async (r) => { setMsgs(r.data); const t = (await api.get("/dm/threads")).data.find((x) => x.user.id === u); setSel(t ? t.user : { id: u, name: "Usuario" }); }).catch(() => {});
  }, [params]); // eslint-disable-line

  const find = async (c) => {
    const v = (c || code).trim().toUpperCase().replace(/^.*(CM-[A-Z0-9]{6}).*$/, "$1");
    if (!v) return;
    try { const { data } = await api.get(`/users/by-code/${v}`); if (data.id === user.id) return toast.error("Ese es tu propio código"); setSel(data); setCode(""); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async (text, image_url) => { await api.post(`/dm/${sel.id}/messages`, { text, image_url }); loadMsgs(); loadThreads(); };

  return (
    <div className="space-y-5">
      <Section title="Mensajes" subtitle="Chatea con cualquier usuario usando su código CM-XXXXXX" />
      <div className="flex gap-2">
        <Input data-testid="dm-code-input" className="h-12 font-mono uppercase" placeholder="Código del usuario (CM-XXXXXX)" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && find()} />
        <Button data-testid="dm-scan-button" variant="outline" className="h-12" onClick={() => setScan(true)}><ScanLine size={18} /></Button>
        <Button data-testid="dm-find-button" className="h-12 bg-primary text-primary-foreground hover:bg-cyan-400" onClick={() => find()}><Search size={18} className="mr-1" /> Buscar</Button>
      </div>
      <QrScanner open={scan} onClose={() => setScan(false)} onScan={(t) => { setScan(false); find(t); }} />
      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <div className="neon-card divide-y divide-border/60 max-h-[60vh] overflow-auto">
          {threads.length === 0 && <div className="p-4 text-sm text-muted-foreground">Sin conversaciones. Busca a alguien por su código.</div>}
          {threads.map((t) => (
            <button key={t.user.id} data-testid={`dm-thread-${t.user.id}`} onClick={() => setSel(t.user)} className={`w-full text-left p-3 hover:bg-secondary/40 transition-colors ${sel?.id === t.user.id ? "bg-primary/10" : ""}`}>
              <div className="flex items-center justify-between"><span className="font-semibold text-sm truncate">{t.user.name}</span>{t.unread > 0 && <span className="rounded-full bg-amber-500 text-background text-[10px] font-bold px-2 py-0.5">{t.unread}</span>}</div>
              <div className="text-xs text-muted-foreground font-mono">{t.user.user_code}</div>
              <div className="text-xs text-muted-foreground truncate">{t.last_text || "Imagen adjunta"} · {fmtDate(t.last_at)}</div>
            </button>
          ))}
        </div>
        <div>{sel ? <><div className="font-semibold mb-2" data-testid="dm-header">Chat con {sel.name} <span className="text-xs text-muted-foreground font-mono">{sel.user_code}</span></div><ChatBox messages={msgs.map((m) => ({ ...m, user_id: m.from_id }))} onSend={send} meId={user.id} testId="dm-chat" /></> : <Empty text="Selecciona una conversación o busca un usuario." />}</div>
      </div>
    </div>
  );
}
